'use client';

import React from 'react';
import Link from 'next/link';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import {
  Check,
  CheckCircle2,
  X,
  ArrowRight,
  ShieldCheck,
  Zap,
  HelpCircle,
  MessageCircle,
  Sparkles
} from 'lucide-react';

export default function PreciosSaaSPage() {
  return (
    <div className="min-h-screen bg-[#F7F7FA] text-[#191923] flex flex-col font-sans selection:bg-[#6255F5] selection:text-white">
      {/* Header */}
      <nav className="w-full bg-white/90 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <CalenddyLogo href="/" size="md" />

          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <Link href="/" className="hover:text-[#6255F5] transition-colors">
              Inicio
            </Link>
            <Link href="/funciones" className="hover:text-[#6255F5] transition-colors">
              Funciones
            </Link>
            <Link href="/precios" className="text-[#6255F5] font-bold">
              Precios
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/ingresar"
              className="text-xs sm:text-sm font-bold text-slate-700 hover:text-[#6255F5] px-3 py-2 rounded-xl transition-colors"
            >
              Ingresar
            </Link>
            <Link
              href="/registro"
              className="px-4 sm:px-5 py-2.5 bg-[#6255F5] hover:bg-[#5144E5] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-md shadow-[#6255F5]/25"
            >
              Crear cuenta
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-12">
          {/* Hero Precios */}
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ECEAFF] text-[#6255F5] text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              Suscripción de Software SaaS
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-[#191923] tracking-tight">
              Planes simples y transparentes
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              El costo de tu software de gestión. Sin porcentaje sobre tus turnos ni comisiones ocultas. Todo lo que cobrás a tus clientes es 100% tuyo.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {/* Plan Inicial */}
            <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/90 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Profesional Independiente
                </span>
                <h3 className="text-xl font-bold text-slate-900">Plan Inicial</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-slate-900">$18.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  Para quienes trabajan solos y necesitan automatizar turnos y recordatorios.
                </p>

                <div className="pt-2 border-t border-slate-100 space-y-2.5 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="font-semibold text-slate-900">1 Profesional</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Turnos y reservas online ilimitadas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Enlace público <code className="text-[#6255F5]">/tu-slug</code></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>WhatsApp Recordatorios automáticos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Gestión de clientes y fichas</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <X className="w-4 h-4 shrink-0" />
                    <span>Cobro de señas online con Mercado Pago</span>
                  </div>
                </div>
              </div>

              <Link
                href="/registro"
                className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl transition text-center"
              >
                Comenzar con Plan Inicial
              </Link>
            </div>

            {/* Plan Pro (Destacado) */}
            <div className="bg-white rounded-3xl p-6 sm:p-7 border-2 border-[#6255F5] shadow-xl relative flex flex-col justify-between space-y-6">
              <div className="absolute -top-3.5 right-6 bg-[#6255F5] text-white text-[10px] font-black uppercase tracking-wider px-3.5 py-1 rounded-full shadow-sm">
                Más recomendado
              </div>

              <div className="space-y-4">
                <span className="text-xs font-bold text-[#6255F5] uppercase tracking-wide">
                  Salones, Barberías y Estudios
                </span>
                <h3 className="text-xl font-bold text-slate-900">Plan Profesional</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-[#6255F5]">$34.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  La solución integral para equipos que quieren cobrar señas y controlar su caja.
                </p>

                <div className="pt-2 border-t border-slate-100 space-y-2.5 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span className="font-bold text-slate-900">Hasta 5 Profesionales</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span className="font-semibold text-slate-900">Cobro de señas automático (Mercado Pago)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span>WhatsApp QR multitenant con tu número</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span>Caja diaria, arqueo y control de inventario</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span>Vouchers, cupones y programa de puntos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6255F5] shrink-0" />
                    <span>Métricas y estadísticas de rendimiento</span>
                  </div>
                </div>
              </div>

              <Link
                href="/registro"
                className="w-full py-3 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-xs rounded-xl transition text-center shadow-lg shadow-[#6255F5]/25"
              >
                Comenzar con Plan Pro →
              </Link>
            </div>

            {/* Plan Empresa */}
            <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/90 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  Franquicias y Multi-Sedes
                </span>
                <h3 className="text-xl font-bold text-slate-900">Plan Empresa</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-slate-900">$59.900</span>
                  <span className="text-xs text-slate-500">/ mes (ARS)</span>
                </div>
                <p className="text-xs text-slate-500">
                  Para cadenas con múltiples sucursales, más de 10 profesionales y soporte dedicado.
                </p>

                <div className="pt-2 border-t border-slate-100 space-y-2.5 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="font-semibold text-slate-900">Profesionales ilimitados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Multi-sucursal y multi-caja integrada</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Múltiples números de WhatsApp vinculados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Exportación de datos y auditoría completa</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Soporte prioritario por WhatsApp</span>
                  </div>
                </div>
              </div>

              <Link
                href="/registro"
                className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl transition text-center"
              >
                Consultar por Plan Empresa
              </Link>
            </div>
          </div>

          {/* Banner Aclaratorio Diferenciador */}
          <div className="bg-[#ECEAFF] border border-[#d6d0ff] rounded-2xl p-5 text-center text-xs text-[#6255F5] font-medium leading-relaxed">
            💡 <strong>Diferencia clave:</strong> Los precios mostrados arriba corresponden al abono mensual por el uso del sistema Calenddy. Cada negocio define sus propios precios, promociones y señas de servicios, los cuales son abonados directamente por sus clientes sin intermediación ni retenciones.
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-[#191923] text-white py-10 px-4 sm:px-6 text-center text-xs text-slate-400">
        <div className="max-w-4xl mx-auto space-y-3">
          <CalenddyLogo variant="light" size="sm" href="/" className="justify-center" />
          <p>© 2026 Calenddy. Plataforma SaaS de Gestión y Reservas.</p>
        </div>
      </footer>
    </div>
  );
}
